import React, { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ChevronDownIcon } from 'lucide-react';
interface FAQItem {
  question: string;
  answer: string;
}
const faqs: FAQItem[] = [{
  question: 'Does this involve leaving my child to scream?',
  answer: 'No. We use "The Quiet Return," which means you are present and supportive, but boring and consistent. Your child is never left alone to cry — you are simply removing the "Jackpot" reward that has been reinforcing the waking behavior.'
}, {
  question: 'What if we share a room?',
  answer: 'The guide includes a specific modification for room-sharing families to ensure the "Visual Break" necessary for success. Room-sharing does not disqualify you from this method.'
}, {
  question: 'How long does it take?',
  answer: 'The core reset takes 72 hours. While some habits take a few extra days to cement, the biological shift happens within the first three nights. Most parents see dramatic improvement by Night 2.'
}, {
  question: 'Is it safe?',
  answer: 'Yes. The protocol is based on standard pediatric sleep hygiene and nutritional timing. There are no supplements, no medications, and no techniques that conflict with mainstream pediatric guidance.'
}, {
  question: 'My child is 4 years old. Is it too late?',
  answer: 'Not at all. The Circadian Anchor method works for children up to 4 years old. The biological mechanisms are the same — the protocol simply accounts for the increased willpower and verbal ability of older toddlers.'
}];
function FAQItem({
  question,
  answer
}: FAQItem) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b" style={{
    borderColor: 'rgba(201,169,110,0.2)'
  }} data-id="element-1">
      <button className="w-full flex items-center justify-between py-5 text-left gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 rounded" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} data-id="element-2">
        <span className="text-base md:text-lg font-semibold" style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1a2744'
      }} data-id="element-3">
          {question}
        </span>
        <ChevronDownIcon className="w-5 h-5 flex-shrink-0 transition-transform duration-300" style={{
        color: '#c9a96e',
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
      }} aria-hidden="true" data-id="element-4" />
      </button>
      <div className="overflow-hidden transition-all duration-300" style={{
      maxHeight: isOpen ? '300px' : '0px'
    }} data-id="element-5">
        <p className="pb-5 text-sm md:text-base leading-relaxed" style={{
        color: '#6b6560',
        fontFamily: "'Inter', sans-serif"
      }} data-id="element-6">
          {answer}
        </p>
      </div>
    </div>;
}
export function FAQSection() {
  const ref = useScrollReveal();
  return <section ref={ref} className="w-full section-padding" style={{
    backgroundColor: '#f0ebe2'
  }} aria-label="Frequently asked questions" data-id="element-7">
      <div className="max-w-2xl mx-auto" data-id="element-8">
        {/* Label */}
        <div className="scroll-reveal text-center mb-10" data-id="element-9">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full" style={{
          color: '#c9a96e',
          backgroundColor: 'rgba(201,169,110,0.12)',
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-10">
            Questions & Answers
          </span>
        </div>

        {/* Heading */}
        <div className="scroll-reveal scroll-reveal-delay-1 text-center mb-4" data-id="element-11">
          <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{
          fontFamily: "'Playfair Display', serif",
          color: '#1a2744'
        }} data-id="element-12">
            Your Questions, Answered
          </h2>
        </div>

        <div className="scroll-reveal scroll-reveal-delay-2 gold-divider mb-12" data-id="element-13" />

        {/* FAQ accordion */}
        <div className="scroll-reveal scroll-reveal-delay-3 rounded-2xl overflow-hidden p-6 md:p-8" style={{
        backgroundColor: '#ffffff',
        boxShadow: '0 4px 24px rgba(26,39,68,0.08)'
      }} data-id="element-14">
          {faqs.map(faq => <FAQItem key={faq.question} question={faq.question} answer={faq.answer} data-id="element-15" />)}
        </div>
      </div>
    </section>;
}